/**
 * Documentele și pozele unui eveniment, ale unei campanii sau ale unei acțiuni de teren.
 *
 * Un singur buton, „Adaugă” (Madalin, 15.10.2026: „să poată încărca direct fiecare ce document
 * sau ce poză vrea, fără să mai stea să specifice — oricum se vede ce este din extensie și nume").
 * Fără selector de fel și fără etichete: pozele se văd ca miniaturi, documentele ca listă.
 *
 * Felul se pune totuși în spate, singur (`ghicesteFelDocument`): pozele se micșorează în browser,
 * iar sponsorizarea cere un contract recunoscut (`verificaContractSponsorizare`, Legea 32/1994).
 * Pe o sponsorizare, fiecare document are butonul „Contract" — un „scan.pdf" nu s-ar ghici din nume.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { ChevronLeft, ChevronRight, Download, Paperclip, Plus, Trash2, X } from 'lucide-react'
import { deCeNuSeIncarca, esteFelDePoza, ghicesteFelDocument, type FelDocument } from '@b4l/shared'
import { api } from '../../lib/api'
import { micsoreazaPoza } from '../../lib/micsoreazaPoza'
import { useConfirmare } from '../ui/Confirmare'
import { PozaMare } from './PozaMare'

export interface Document {
  id: number
  fel: FelDocument
  numeFisier: string
  mimeType: string
  octeti: number
  miniatura: string | null
  descriere: string | null
  incarcatDe: string
  createdAt: string
}

interface Props {
  tip: 'campanie' | 'actiune'
  parinteId: number
  canWrite: boolean
  /** Activitate de tip sponsorizare: documentele se pot marca drept contractul ei. */
  contractSponsorizare?: boolean
}

function kb(octeti: number): string {
  // „0 KB" la un fisier de 300 de octeti pare gol; gol e doar cel cu 0.
  if (octeti > 0 && octeti < 1024) return '<1 KB'
  return octeti < 1024 * 1024
    ? `${Math.round(octeti / 1024)} KB`
    : `${(octeti / 1024 / 1024).toFixed(1)} MB`
}

const FORMATE = '.pdf,.doc,.docx,.xls,.xlsx,image/jpeg,image/png,image/webp'

export function DocumenteContainer({ tip, parinteId, canWrite, contractSponsorizare }: Props) {
  const qc = useQueryClient()
  const { confirma } = useConfirmare()
  const cheie = ['poze', tip, parinteId]
  const inputRef = useRef<HTMLInputElement>(null)
  const [deschisaId, setDeschisaId] = useState<number | null>(null)
  const [urca, setUrca] = useState<{ facute: number; total: number } | null>(null)
  /** Ce n-a intrat la ultima incarcare, cu motivul — ramane pana la urmatoarea. */
  const [cazute, setCazute] = useState<{ nume: string; motiv: string }[]>([])
  const [trage, setTrage] = useState(false)

  const q = useQuery({
    queryKey: cheie,
    queryFn: async () =>
      (await api.get<{ poze: Document[] }>(`/marketing/poze/${tip}/${parinteId}`)).data.poze,
  })

  const sterge = useMutation({
    mutationFn: async (id: number) => api.delete(`/marketing/poze/${id}`),
    onSuccess: () => {
      setDeschisaId(null)
      void qc.invalidateQueries({ queryKey: cheie })
    },
    onError: () => toast.error('Fișierul nu s-a putut șterge.'),
  })

  const contract = useMutation({
    mutationFn: async (v: { id: number; contract: boolean }) =>
      api.patch(`/marketing/poze/${v.id}/contract`, { contract: v.contract }),
    onSuccess: (_r, v) => {
      toast.success(v.contract ? 'Marcat drept contract.' : 'Nu mai e marcat drept contract.')
      void qc.invalidateQueries({ queryKey: cheie })
    },
    onError: (e) =>
      toast.error(
        (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
          ?.message ?? 'Nu s-a putut schimba.',
      ),
  })

  /*
    Fișierele se urcă imediat ce sunt alese, UNUL CÂTE UNUL: zece cereri paralele de pe o
    conexiune mobilă cad pe rând, iar omul nu știe care a intrat. Ce nu se poate încărca
    (format, mărime) se spune înainte de urcare; ce cade la server, după — cu motivul.
  */
  async function adauga(lista: FileList | File[] | null): Promise<void> {
    if (!lista || lista.length === 0 || urca) return
    const fisiere = Array.from(lista)
    if (inputRef.current) inputRef.current.value = ''
    const probleme: { nume: string; motiv: string }[] = []
    const deUrcat = fisiere.filter((f) => {
      const motiv = deCeNuSeIncarca(ghicesteFelDocument(f.name, f.type), f.type, f.size)
      if (motiv) probleme.push({ nume: f.name, motiv })
      return !motiv
    })
    setUrca({ facute: 0, total: deUrcat.length })
    let reusite = 0
    for (const [i, f] of deUrcat.entries()) {
      const fel = ghicesteFelDocument(f.name, f.type)
      try {
        const fd = new FormData()
        if (esteFelDePoza(fel)) {
          // Doar pozele se micșorează. Un PDF trecut prin canvas ar ieși poză, nu document.
          let m: Awaited<ReturnType<typeof micsoreazaPoza>>
          try {
            m = await micsoreazaPoza(f)
          } catch {
            throw new Error('poza nu s-a putut deschide în browser — încearcă JPG')
          }
          fd.append('file', m.fisier)
          fd.append('miniatura', m.miniatura)
        } else {
          fd.append('file', f)
        }
        fd.append('fel', fel)
        await api.post(`/marketing/poze/${tip}/${parinteId}`, fd)
        reusite++
      } catch (e) {
        probleme.push({
          nume: f.name,
          motiv:
            (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
              ?.message ??
            (e as Error)?.message ??
            'nu s-a putut încărca',
        })
      }
      setUrca({ facute: i + 1, total: deUrcat.length })
    }
    setUrca(null)
    setCazute(probleme)
    await qc.invalidateQueries({ queryKey: cheie })
    if (reusite > 0)
      toast.success(reusite === 1 ? 'Fișierul a intrat.' : `Au intrat ${reusite} fișiere.`)
    if (probleme.length > 0)
      toast.error(
        probleme.length === 1
          ? 'Un fișier nu s-a încărcat — vezi motivul.'
          : `${probleme.length} fișiere nu s-au încărcat — vezi motivele.`,
      )
  }

  async function descarca(d: Document): Promise<void> {
    try {
      const r = await api.get(`/marketing/poze/continut/${d.id}`, { responseType: 'blob' })
      const url = URL.createObjectURL(r.data as Blob)
      const a = document.createElement('a')
      a.href = url
      a.download = d.numeFisier
      a.click()
      URL.revokeObjectURL(url)
    } catch {
      toast.error('Fișierul nu s-a putut descărca.')
    }
  }

  async function cereStergerea(d: Document): Promise<void> {
    if (
      await confirma({
        titlu: `Ștergi „${d.numeFisier}”?`,
        mesaj: 'Fișierul dispare de aici.',
        buton: 'Șterge',
        pericol: true,
      })
    )
      sterge.mutate(d.id)
  }

  const documente = useMemo(() => q.data ?? [], [q.data])
  const poze = documente.filter((d) => esteFelDePoza(d.fel))
  const restul = documente.filter((d) => !esteFelDePoza(d.fel))
  const areContract = restul.some((d) => d.fel === 'contract')
  const indexDeschisa = poze.findIndex((p) => p.id === deschisaId)
  const deschisa = indexDeschisa >= 0 ? poze[indexDeschisa] : null

  const muta = useCallback(
    (pas: number) => {
      if (indexDeschisa < 0 || poze.length < 2) return
      setDeschisaId(poze[(indexDeschisa + pas + poze.length) % poze.length].id)
    },
    [indexDeschisa, poze],
  )
  useEffect(() => {
    if (!deschisa) return
    const k = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDeschisaId(null)
      else if (e.key === 'ArrowRight') muta(1)
      else if (e.key === 'ArrowLeft') muta(-1)
    }
    window.addEventListener('keydown', k)
    return () => window.removeEventListener('keydown', k)
  }, [deschisa, muta])

  // Se poate si trage un fisier peste zona; butonul ramane calea principala.
  const tragere = canWrite
    ? {
        onDragOver: (e: DragEvent) => {
          e.preventDefault()
          setTrage(true)
        },
        onDragLeave: () => setTrage(false),
        onDrop: (e: DragEvent) => {
          e.preventDefault()
          setTrage(false)
          void adauga(e.dataTransfer.files)
        },
      }
    : {}

  return (
    <div
      {...tragere}
      className={`grid gap-3 rounded-xl ${trage ? 'outline-dashed outline-2 outline-offset-4 outline-b4l-blue' : ''}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-mic text-b4l-text-muted">
          {documente.length === 0
            ? 'Niciun document sau poză încă.'
            : [
                poze.length && `${poze.length} ${poze.length === 1 ? 'poză' : 'poze'}`,
                restul.length &&
                  `${restul.length} ${restul.length === 1 ? 'document' : 'documente'}`,
              ]
                .filter(Boolean)
                .join(' · ')}
        </span>
        {canWrite && (
          <>
            <input
              ref={inputRef}
              type="file"
              accept={FORMATE}
              multiple
              className="hidden"
              onChange={(e) => void adauga(e.target.files)}
            />
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={urca !== null}
              className="inline-flex items-center gap-1.5 rounded-caseta border border-b4l-border bg-b4l-surface px-3 py-1.5 text-corp font-medium hover:border-b4l-blue disabled:opacity-50"
            >
              <Plus size={15} />
              {urca ? `Se încarcă ${urca.facute}/${urca.total}…` : 'Adaugă'}
            </button>
          </>
        )}
      </div>

      {cazute.length > 0 && (
        <div className="grid gap-1 rounded-lg bg-b4l-red-light px-3 py-2 text-mic text-b4l-red-dark">
          <div className="flex items-start justify-between gap-2">
            <b>Nu s-au încărcat:</b>
            <button
              type="button"
              onClick={() => setCazute([])}
              aria-label="Ascunde"
              className="rounded p-0.5 hover:bg-b4l-red/10"
            >
              <X size={14} />
            </button>
          </div>
          {cazute.map((c, i) => (
            <span key={`${c.nume}-${i}`}>
              {c.nume} — {c.motiv}
            </span>
          ))}
        </div>
      )}

      {contractSponsorizare && canWrite && restul.length > 0 && !areContract && (
        <p className="rounded-lg bg-b4l-amber-light px-3 py-2 text-mic text-b4l-amber-dark">
          Sponsorizarea cere contractul semnat. Apasă „Contract” pe documentul lui — altfel
          expedierea și bonul de consum nu pornesc.
        </p>
      )}

      {q.isLoading && <p className="text-sm text-b4l-text-muted">Se încarcă…</p>}
      {q.isError && (
        <p className="text-sm text-b4l-red-dark">
          Documentele nu s-au putut încărca.{' '}
          <button type="button" onClick={() => void q.refetch()} className="underline">
            Reîncearcă
          </button>
        </p>
      )}

      {/* Pozele se văd; restul se citesc. Două forme, fiindcă sunt două nevoi. */}
      {poze.length > 0 && (
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-6">
          {poze.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => setDeschisaId(p.id)}
              className="aspect-square max-w-full overflow-hidden rounded-lg border border-b4l-border bg-b4l-bg-soft"
              title={`${p.numeFisier} · ${p.incarcatDe}`}
              aria-label={`Deschide poza ${p.numeFisier}`}
            >
              {p.miniatura ? (
                <img src={p.miniatura} alt="" className="h-full w-full object-cover" />
              ) : (
                <span className="flex h-full w-full items-center justify-center text-[10px] text-b4l-text-dim">
                  fără miniatură
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {restul.length > 0 && (
        <ul className="divide-y divide-b4l-border rounded-lg border border-b4l-border">
          {restul.map((d) => (
            <li key={d.id} className="flex items-center gap-2 p-2 text-sm">
              <Paperclip size={14} className="shrink-0 text-b4l-text-muted" aria-hidden />
              <span className="min-w-0 flex-1">
                <span className="block truncate">{d.numeFisier}</span>
                <span className="text-[11px] text-b4l-text-muted">
                  {kb(d.octeti)} · {d.incarcatDe} ·{' '}
                  {new Date(d.createdAt).toLocaleDateString('ro-RO')}
                </span>
              </span>
              {/*
                Butonul se cheama „Contract" (Madalin, 15.10.2026) — un singur cuvant, fara loc de
                interpretare. Verde cu bifa cand documentul e contractul; inca un clic il scoate.
              */}
              {contractSponsorizare && (canWrite || d.fel === 'contract') && (
                <button
                  type="button"
                  disabled={!canWrite || contract.isPending}
                  aria-pressed={d.fel === 'contract'}
                  onClick={() => contract.mutate({ id: d.id, contract: d.fel !== 'contract' })}
                  title={
                    d.fel === 'contract'
                      ? 'E contractul sponsorizării. Apasă ca să scoți marcajul.'
                      : 'Marchează documentul drept contractul sponsorizării'
                  }
                  className={`whitespace-nowrap rounded-full px-2.5 py-0.5 text-[11.5px] disabled:cursor-default ${
                    d.fel === 'contract'
                      ? 'bg-b4l-green-light font-semibold text-b4l-green-dark'
                      : 'border border-b4l-border text-b4l-text-muted hover:border-b4l-blue'
                  }`}
                >
                  {d.fel === 'contract' ? 'Contract ✓' : 'Contract'}
                </button>
              )}
              <button
                type="button"
                onClick={() => void descarca(d)}
                className="rounded p-1.5 text-b4l-text-muted hover:bg-b4l-bg"
                title="Descarcă"
                aria-label={`Descarcă ${d.numeFisier}`}
              >
                <Download size={16} />
              </button>
              {canWrite && (
                <button
                  type="button"
                  onClick={() => void cereStergerea(d)}
                  className="rounded p-1.5 text-b4l-text-muted hover:bg-b4l-bg"
                  title="Șterge"
                  aria-label={`Șterge ${d.numeFisier}`}
                >
                  <Trash2 size={16} />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {deschisa && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={deschisa.numeFisier}
          className="fixed inset-0 z-50 flex flex-col bg-black/85 p-4"
          onClick={() => setDeschisaId(null)}
        >
          <div
            className="flex items-center justify-between gap-3 pb-2 text-sm text-white"
            onClick={(e) => e.stopPropagation()}
          >
            <span className="truncate">
              {deschisa.numeFisier}
              <span className="ml-2 text-white/60">
                {indexDeschisa + 1} din {poze.length} · {kb(deschisa.octeti)} ·{' '}
                {deschisa.incarcatDe} · {new Date(deschisa.createdAt).toLocaleDateString('ro-RO')}
              </span>
            </span>
            <span className="flex shrink-0 items-center gap-2">
              {canWrite && (
                <button
                  type="button"
                  onClick={() => void cereStergerea(deschisa)}
                  className="rounded p-1.5 text-white/80 hover:bg-white/10"
                  title="Șterge"
                  aria-label="Șterge poza"
                >
                  <Trash2 size={18} />
                </button>
              )}
              <button
                type="button"
                onClick={() => setDeschisaId(null)}
                className="rounded p-1.5 text-white/80 hover:bg-white/10"
                title="Închide"
                aria-label="Închide"
              >
                <X size={18} />
              </button>
            </span>
          </div>
          <div className="relative flex min-h-0 flex-1" onClick={(e) => e.stopPropagation()}>
            <PozaMare id={deschisa.id} alt={deschisa.numeFisier} />
            {poze.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={() => muta(-1)}
                  aria-label="Poza dinainte"
                  className="absolute left-1 top-1/2 -translate-y-1/2 rounded-full bg-black/50 p-2 text-white hover:bg-black/70"
                >
                  <ChevronLeft size={22} />
                </button>
                <button
                  type="button"
                  onClick={() => muta(1)}
                  aria-label="Poza următoare"
                  className="absolute right-1 top-1/2 -translate-y-1/2 rounded-full bg-black/50 p-2 text-white hover:bg-black/70"
                >
                  <ChevronRight size={22} />
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
